import axios, { isAxiosError } from 'axios';
import {
  getApiBaseUrl,
  getEmbeddedNavianceHighschoolIdHeader,
  getHighschoolId,
  isEmbeddedInSucceed,
  isQaSucceedApiProxyDev,
} from '../utils/cp-context';
import {
  type SucceedUserSessionResponse,
  SUCCEED_PRODUCT_COURSE_PLANNER_NEXT,
} from '../types/succeed-user-session';

const USER_SESSION_TOAST = {
  heading: 'Unable to load your user profile',
  sessionExpired: 'Your session may have expired. Refresh the page and sign in again.',
  forbidden:
    'You may not have permission to use Course Planner for this school or district context.',
  notFound: 'The user profile service did not respond as expected. Try refreshing the page.',
  serverError: 'The server had a problem loading your profile. Please try again in a few minutes.',
  network:
    'We could not reach Succeed (network error or timeout). Check your connection and refresh the page.',
  generic:
    'Course Planner could not load your account from Succeed. You can keep working; if something looks wrong, refresh the page.',
} as const;

/** `undefined` = never loaded successfully; otherwise last successful response (may be `null`). */
let succeedUserSessionCache: SucceedUserSessionResponse | null | undefined = undefined;
let succeedUserSessionInFlight: Promise<SucceedUserSessionResponse | null> | null = null;
/** Incremented on {@link resetSucceedUserSessionCache} so in-flight responses cannot repopulate a cleared cache. */
let succeedUserSessionCacheGeneration = 0;

/**
 * Clears the in-memory session cache (e.g. logout or tests). Next {@link fetchSucceedUserSession} performs a real GET.
 * In-flight requests that complete after this call will not write to the cache (generation mismatch).
 */
export function resetSucceedUserSessionCache(): void {
  succeedUserSessionCacheGeneration += 1;
  succeedUserSessionCache = undefined;
  succeedUserSessionInFlight = null;
}

/**
 * True when axios reports a cancelled request (`ERR_CANCELED`), e.g. an explicit `AbortSignal` from another caller.
 * This module does not pass `signal` to `GET /user`; deduping uses a generation counter instead of aborting the shared request.
 */
export function isSucceedUserSessionRequestAborted(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false;
  }
  return error.code === 'ERR_CANCELED';
}

/** User-facing copy for toast when GET /user?product=7 fails. */
export function getSucceedUserSessionErrorToast(error: unknown): {
  heading: string;
  subheading: string;
} {
  if (!isAxiosError(error)) {
    return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.generic };
  }
  const status = error.response?.status;
  if (status === 401) {
    return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.sessionExpired };
  }
  if (status === 403) {
    return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.forbidden };
  }
  if (status === 404) {
    return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.notFound };
  }
  if (status !== undefined && status >= 500) {
    return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.serverError };
  }
  if (error.response === undefined) {
    return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.network };
  }
  return { heading: USER_SESSION_TOAST.heading, subheading: USER_SESSION_TOAST.generic };
}

async function performSucceedUserSessionRequest(): Promise<SucceedUserSessionResponse | null> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const headers: Record<string, string> = {
    Accept: 'application/json, text/plain, */*',
  };

  if (isEmbeddedInSucceed()) {
    headers['naviance-highschool-id'] = getEmbeddedNavianceHighschoolIdHeader();
  } else if (isQaSucceedApiProxyDev() && import.meta.env.VITE_APP_TARGET !== 'student') {
    const highschoolId = getHighschoolId();
    if (highschoolId) {
      headers['naviance-highschool-id'] = highschoolId;
      headers['x-user-authorized-schools'] = highschoolId;
    }
  }

  const { data } = await axios.get<SucceedUserSessionResponse>(`${base}/user`, {
    params: { product: SUCCEED_PRODUCT_COURSE_PLANNER_NEXT },
    withCredentials: isEmbeddedInSucceed(),
    headers,
    timeout: 30000,
  });

  return data ?? null;
}

/**
 * Staff Succeed session: GET `/user?product=7` on succeed-api (same as legacy c-p-ui `userSession.sagas`).
 * Uses cookie auth when embedded (`withCredentials`) and the same `naviance-highschool-id` rules as {@link createApiClient}.
 *
 * **Deduped:** concurrent callers and React 18 StrictMode (double effect) share one HTTP request.
 * Successful responses are cached for the page until {@link resetSucceedUserSessionCache}.
 * Previously, the first effect’s `AbortController` plus a second effect started two GETs and could break QA proxies.
 */
export async function fetchSucceedUserSession(): Promise<SucceedUserSessionResponse | null> {
  if (!isEmbeddedInSucceed() && !isQaSucceedApiProxyDev()) {
    return null;
  }
  if (succeedUserSessionCache !== undefined) {
    return succeedUserSessionCache;
  }
  if (!succeedUserSessionInFlight) {
    const generationWhenStarted = succeedUserSessionCacheGeneration;
    const flight = performSucceedUserSessionRequest()
      .then((data) => {
        if (generationWhenStarted !== succeedUserSessionCacheGeneration) {
          return null;
        }
        succeedUserSessionCache = data;
        return data;
      })
      .finally(() => {
        if (succeedUserSessionInFlight === flight) {
          succeedUserSessionInFlight = null;
        }
      });
    succeedUserSessionInFlight = flight;
  }
  return succeedUserSessionInFlight;
}
